import {
  Button,
  Card,
  Field,
  Input,
  SkeletonLoader,
  Textarea,
  H4,
} from '@payaca/ambrosia';
import ShadowDomRenderer from '@payaca/components/shadowDomRenderer/ShadowDomRenderer';
import { FC, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod/v4';

import SenderChoiceField from '@/ui/components/senderChoice/SenderChoiceField';
import { type SenderChoiceControl } from '@/ui/components/senderChoice/useSenderChoice';
import { type Contact } from './contacts';

export const DRAFT_SEND_EMAIL_PREVIEW_SCHEMA = z.object({
  intro: z.string(),
  outro: z.string(),
  recipientEmail: z.email({ error: 'Enter a valid email address' }),
});

export type DraftSendEmailPreviewValues = z.infer<
  typeof DRAFT_SEND_EMAIL_PREVIEW_SCHEMA
>;

type DraftSendEmailPreviewFieldsetProps = {
  isLoading: boolean;
  recipient: Contact | null;
  editableRecipient: boolean;
  /**
   * The noun shown in the preview's "View" button, matching the sent email's
   * call-to-action.
   */
  documentNoun: 'quote' | 'estimate';
  logoUrl?: string | null;
  /**
   * Who the email is sent as, shown as a picker where more than one sender is
   * available. The pick is the user's own remembered preference, so it also
   * applies to their later sends.
   */
  senderChoice: SenderChoiceControl;
  subject?: string;
  /**
   * Default email copy. The fieldset re-syncs its fields whenever these or
   * the recipient identity change (keeping dirty fields), so recipient-name
   * templating is the parent's job: recompute the defaults per recipient.
   * Ignored when `customEmailMarkup` is set.
   */
  defaultIntro?: string;
  defaultOutro?: string;
  /**
   * Pre-rendered HTML for the whole email body (an Account-level custom email
   * template). When set, it replaces the editable intro/outro body. Must come
   * from the backend's trusted rendering pipeline — the shadow DOM isolates
   * styles only, not scripts.
   */
  customEmailMarkup?: string | null;
};

const DraftSendEmailPreviewFieldset: FC<DraftSendEmailPreviewFieldsetProps> = (
  props
) => {
  const {
    recipient,
    editableRecipient,
    documentNoun,
    isLoading,
    logoUrl,
    senderChoice,
    subject,
    defaultIntro = '',
    defaultOutro = '',
    customEmailMarkup,
  } = props;

  const { getValues, reset } = useFormContext<DraftSendEmailPreviewValues>();

  // Re-sync defaults when the recipient or the parent-computed copy changes so
  // the email body references the right contact/sender. Keep dirty fields so
  // user edits aren't lost. A whole-form reset (rather than per-field
  // resetField) is required because hidden fields (e.g. recipientEmail outside
  // bounced state) aren't registered; spreading getValues() preserves any
  // fields the parent form added beyond this fieldset's.
  useEffect(() => {
    reset(
      {
        ...getValues(),
        intro: defaultIntro,
        outro: defaultOutro,
        recipientEmail: recipient?.email ?? '',
      },
      { keepDirtyValues: true }
    );
  }, [
    recipient?.publicId,
    recipient?.email,
    defaultIntro,
    defaultOutro,
    getValues,
    reset,
  ]);

  return (
    <div className="space-y-4">
      <H4>Email preview</H4>

      <Card>
        <Card.Body>
          <div className="-mx-4 border-b border-gray-200 px-4 pb-4 md:-mx-5 md:px-5">
            {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Mixed grid template has no utility */}
            <div className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2">
              <span className="font-medium text-gray-500">To:</span>
              {editableRecipient ? (
                <Controller<DraftSendEmailPreviewValues, 'recipientEmail'>
                  name="recipientEmail"
                  render={({ field: { name, ...attributes } }) => (
                    <Field name={name}>
                      <Field.Label className="sr-only">
                        Recipient email
                      </Field.Label>

                      <Input inputMode="email" {...attributes} />
                    </Field>
                  )}
                />
              ) : (
                <span className="text-gray-900">
                  {recipient && recipient.email
                    ? `${recipient.name ?? 'Customer'} <${recipient.email}>`
                    : (recipient?.name ?? 'Customer')}
                </span>
              )}

              <span className="font-medium text-gray-500">From:</span>
              <SenderChoiceField {...senderChoice} />
              <span className="font-medium text-gray-500">Subject:</span>
              {subject ? (
                <span
                  data-testid="email-subject"
                  aria-label="Email subject"
                  className="font-semibold text-gray-900"
                >
                  {subject}
                </span>
              ) : (
                <SkeletonLoader.Text className="w-3/4" />
              )}
            </div>
          </div>

          {customEmailMarkup ? (
            <div className="mt-4">
              <ShadowDomRenderer html={customEmailMarkup} />
            </div>
          ) : (
            <EditableEmailBody
              isLoading={isLoading}
              documentNoun={documentNoun}
              logoUrl={logoUrl}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

type EditableEmailBodyProps = {
  isLoading: boolean;
  documentNoun: 'quote' | 'estimate';
  logoUrl?: string | null;
};

const EditableEmailBody: FC<EditableEmailBodyProps> = (props) => {
  const { isLoading, documentNoun, logoUrl } = props;

  return (
    <div className="mt-4 space-y-4">
      {logoUrl && (
        <div className="text-center">
          <img
            className="mx-auto h-auto max-w-52"
            src={logoUrl}
            alt="Company logo"
          />
        </div>
      )}

      {isLoading ? (
        <SkeletonLoader.Textarea />
      ) : (
        <Controller<DraftSendEmailPreviewValues, 'intro'>
          name="intro"
          render={({ field: { name, ...attributes } }) => (
            <Field name={name}>
              <Field.Label className="sr-only">Email intro</Field.Label>

              <Textarea {...attributes} autoHeight className="text-center" />
            </Field>
          )}
        />
      )}
      <div className="text-center">
        {/* The sent email's button colour is fixed by the Postmark template,
            not the account's brand or the Ambrosia theme, so override the
            preview button to match it. */}
        <Button disabled className="bg-blue-500!">
          View {documentNoun}
        </Button>
      </div>

      {isLoading ? (
        <SkeletonLoader.Textarea />
      ) : (
        <Controller<DraftSendEmailPreviewValues, 'outro'>
          name="outro"
          render={({ field: { name, ...attributes } }) => (
            <Field name={name}>
              <Field.Label className="sr-only">Email outro</Field.Label>

              <Textarea {...attributes} autoHeight className="text-center" />
            </Field>
          )}
        />
      )}
    </div>
  );
};

export default DraftSendEmailPreviewFieldset;
